import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAppKit, useAppKitAccount } from '@reown/appkit/react';
import { useSignMessage } from 'wagmi';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, Sparkles, Zap, Layers } from 'lucide-react';

export function ProfileSection() {
  const { userProfile, refreshBalance } = useAuth();
  
  const [fullName, setFullName] = useState(userProfile?.full_name || '');
  const [email, setEmail] = useState(userProfile?.email || '');
  const [wallet, setWallet] = useState(userProfile?.wallet_address || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [waitingSignature, setWaitingSignature] = useState(false);

  const { open } = useAppKit();
  const { address, isConnected } = useAppKitAccount();
  const { signMessageAsync } = useSignMessage();

  useEffect(() => {
    if (isConnected && address && !userProfile?.wallet_address) {
      setWallet(address);
    }
  }, [isConnected, address, userProfile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSubmitting(true);

    if (!fullName.trim()) {
      setError('Full Name is required');
      setIsSubmitting(false);
      return;
    }
    if (!email.trim()) {
      setError('Email is required');
      setIsSubmitting(false);
      return;
    }
    if (!wallet.trim()) {
      setError('Wallet Address is required. Please connect your wallet.');
      setIsSubmitting(false);
      return;
    }

    try {
      let finalWallet = wallet.toLowerCase();

      if (wallet && wallet.toLowerCase() !== userProfile?.wallet_address?.toLowerCase()) {
        if (!isConnected || address?.toLowerCase() !== wallet.toLowerCase()) {
           setError('Please connect the wallet you are trying to link.');
           setIsSubmitting(false);
           return;
        }

        setWaitingSignature(true);
        try {
          const nonceRes = await fetch('/api/auth/wallet/nonce', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ wallet_address: finalWallet })
          });
          const nonceData = await nonceRes.json();
          if (!nonceRes.ok) throw new Error(nonceData.error || 'Failed to get nonce');

          const signature = await signMessageAsync({ message: nonceData.message });
          
          const res = await fetch('/api/auth/user', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('daggpt_token') || ''}`
            },
            body: JSON.stringify({
              full_name: fullName.trim(),
              email: email.trim(),
              walletAddress: finalWallet,
              profile_completed: true,
              signature,
              message: nonceData.message
            })
          });

          if (!res.ok) {
            const errData = await res.json();
            throw new Error(errData.error || 'Failed to update profile');
          }

        } catch (signErr) {
          setError(signErr.message || 'Signature verification failed');
          setWaitingSignature(false);
          setIsSubmitting(false);
          return;
        }
        setWaitingSignature(false);
      } else {
        const res = await fetch('/api/auth/user', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('daggpt_token') || ''}`
          },
          body: JSON.stringify({
            full_name: fullName.trim(),
            email: email.trim(),
            walletAddress: finalWallet,
            profile_completed: true
          })
        });

        if (!res.ok) {
          const errData = await res.json();
          throw new Error(errData.error || 'Failed to update profile');
        }
      }

      setSuccess('Profile updated successfully! Redirecting...');
      setTimeout(() => {
        window.location.reload();
      }, 1500);

    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const authProvider = userProfile?.auth_provider || '';
  const isWalletLogin = authProvider === 'wallet';

  const isDisabled = isSubmitting || waitingSignature || !fullName || (isWalletLogin ? !email : !wallet);

  return (
    <div style={{ padding: '60px 24px', maxWidth: '1000px', margin: '0 auto', minHeight: '80vh', display: 'flex', alignItems: 'center' }}>
      
      <style>{`
        .glass-panel {
          background: rgba(255, 255, 255, 0.6);
          backdrop-filter: blur(40px);
          -webkit-backdrop-filter: blur(40px);
          border: 1px solid rgba(226, 232, 240, 0.5);
          box-shadow: 0 20px 40px -15px rgba(0,0,0,0.05);
          border-radius: 24px;
          overflow: hidden;
        }
        .dashboard-input {
          width: 100%;
          padding: 16px 20px;
          border-radius: 12px;
          border: 1px solid rgba(203, 213, 225, 0.6);
          background: rgba(255, 255, 255, 0.8);
          outline: none;
          font-size: 15px;
          color: #1e293b;
          transition: all 0.3s ease;
          font-family: inherit;
          box-shadow: inset 0 2px 4px rgba(0,0,0,0.02);
        }
        .dashboard-input:focus {
          border-color: #7c3aed;
          box-shadow: 0 0 0 4px rgba(124, 58, 237, 0.1), inset 0 2px 4px rgba(0,0,0,0.02);
          background: #ffffff;
        }
        .dashboard-input:disabled {
          background: rgba(248, 250, 252, 0.8);
          color: #64748b;
          cursor: not-allowed;
          border-color: rgba(226, 232, 240, 0.8);
        }
        .dashboard-input::placeholder {
          color: #94a3b8;
        }
        .premium-btn {
          background: linear-gradient(135deg, #7c3aed, #6d28d9);
          color: #ffffff;
          border: none;
          border-radius: 12px;
          padding: 16px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 10px 20px -10px rgba(124, 58, 237, 0.5);
        }
        .premium-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 15px 25px -10px rgba(124, 58, 237, 0.6);
        }
        .premium-btn:disabled {
          background: #cbd5e1;
          color: #f8fafc;
          cursor: not-allowed;
          box-shadow: none;
          transform: none;
        }
      `}</style>

      <div style={{ display: 'flex', gap: '0px', flexWrap: 'wrap', width: '100%', boxShadow: '0 20px 40px -10px rgba(0,0,0,0.05)', borderRadius: '24px', background: '#fff', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
        
        {/* Left Column - Welcome Info */}
        <motion.div 
          initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}
          style={{ flex: '1 1 400px', background: 'linear-gradient(135deg, #7c3aed 0%, #4c1d95 100%)', padding: '60px 48px', color: '#fff', display: 'flex', flexDirection: 'column', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}
        >
          {/* Decorative background elements */}
          <div style={{ position: 'absolute', top: -100, right: -100, width: 300, height: 300, background: 'rgba(255,255,255,0.08)', borderRadius: '50%', filter: 'blur(40px)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: -50, left: -50, width: 250, height: 250, background: 'rgba(255,255,255,0.05)', borderRadius: '50%', filter: 'blur(50px)', pointerEvents: 'none' }} />
          
          <ShieldCheck size={56} color="#fff" strokeWidth={1.5} style={{ marginBottom: '32px' }} />
          <h2 style={{ fontSize: '36px', fontWeight: 800, marginBottom: '16px', letterSpacing: '-0.02em', zIndex: 1 }}>Welcome to DAGGPT</h2>
          <p style={{ fontSize: '16px', color: 'rgba(255,255,255,0.85)', lineHeight: 1.6, marginBottom: '40px', zIndex: 1 }}>
            You're just one step away from joining the most advanced multi-modal AI ecosystem. Complete your profile to unlock your <strong style={{ color: '#fff' }}>$2 bonus</strong>.
          </p>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', zIndex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ background: 'rgba(255,255,255,0.15)', padding: '10px', borderRadius: '12px' }}><Sparkles size={20} color="#fff" /></div>
              <span style={{ fontSize: '15px', fontWeight: 500, letterSpacing: '0.01em' }}>Access Premium AI Models</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ background: 'rgba(255,255,255,0.15)', padding: '10px', borderRadius: '12px' }}><Zap size={20} color="#fff" /></div>
              <span style={{ fontSize: '15px', fontWeight: 500, letterSpacing: '0.01em' }}>Generate Images & Video Instantly</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ background: 'rgba(255,255,255,0.15)', padding: '10px', borderRadius: '12px' }}><Layers size={20} color="#fff" /></div>
              <span style={{ fontSize: '15px', fontWeight: 500, letterSpacing: '0.01em' }}>Build Websites Autonomously</span>
            </div>
          </div>
        </motion.div>

        {/* Right Column - Form */}
        <motion.div 
          initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
          style={{ flex: '1 1 450px', padding: '60px 48px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
        >
          <h3 style={{ fontSize: '24px', fontWeight: 700, color: '#0f172a', marginBottom: '8px' }}>Profile Details</h3>
          <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '32px' }}>Please fill in the required details to secure your account.</p>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%' }}>
          
          {/* Full Name */}
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Full Name</label>
            <input 
              type="text" 
              className="dashboard-input"
              value={fullName}
              disabled={!!userProfile?.full_name}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="John Doe"
            />
            {!!userProfile?.full_name && (
              <div style={{ fontSize: '12px', color: '#059669', marginTop: '8px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                Name saved
              </div>
            )}
          </div>

          {/* Email Address - Only shown for Wallet Sign-ins */}
          {isWalletLogin && (
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email Address</label>
              <input 
                type="email" 
                className="dashboard-input"
                value={email}
                disabled={!!userProfile?.email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="john@example.com"
              />
              {!!userProfile?.email && (
                <div style={{ fontSize: '12px', color: '#059669', marginTop: '8px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                  Email address verified
                </div>
              )}
            </div>
          )}

          {/* Wallet Address - Only shown for Non-Wallet Sign-ins */}
          {!isWalletLogin && (
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Web3 Wallet</label>
              
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <input 
                  type="text" 
                  className="dashboard-input"
                  value={wallet}
                  disabled={true}
                  placeholder="Connect wallet to link"
                  style={{ flex: 1 }}
                />
                {!isConnected ? (
                  <button 
                    type="button"
                    onClick={() => open()}
                    style={{ padding: '0 24px', height: '54px', borderRadius: '12px', background: '#0f172a', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 4px 10px rgba(15, 23, 42, 0.2)' }}
                    onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                    onMouseLeave={e => e.currentTarget.style.transform = 'none'}
                  >
                    Connect
                  </button>
                ) : (
                  <button 
                    type="button"
                    onClick={() => open()}
                    style={{ padding: '0 24px', height: '54px', borderRadius: '12px', background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                    onMouseLeave={e => e.currentTarget.style.transform = 'none'}
                  >
                    {address?.slice(0,6)}...{address?.slice(-4)}
                  </button>
                )}
              </div>
              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '8px', lineHeight: 1.4 }}>
                We require a Web3 wallet to manage your DAGGPT credits securely.
              </div>
            </div>
          )}

          <AnimatePresence>
            {error && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                style={{ overflow: 'hidden' }}
              >
                <div style={{ padding: '14px 18px', background: '#fef2f2', color: '#b91c1c', borderRadius: '12px', fontSize: '14px', border: '1px solid #fecaca', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
                  {error}
                </div>
              </motion.div>
            )}
            
            {success && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                style={{ overflow: 'hidden' }}
              >
                <div style={{ padding: '14px 18px', background: '#ecfdf5', color: '#047857', borderRadius: '12px', fontSize: '14px', border: '1px solid #a7f3d0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                  {success}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <button 
            type="submit"
            disabled={isDisabled}
            className="premium-btn"
            style={{ marginTop: '8px' }}
          >
            {waitingSignature ? 'Sign message in wallet...' : isSubmitting ? 'Saving Profile...' : 'Complete Profile & Claim $2 Bonus'}
          </button>
        </form>
        </motion.div>
      </div>
    </div>
  );
}
